import styles from './FormRadioGroup.module.css';

/**
 * FormRadioGroup — single-select radio group per Henaket spec.
 *
 * 0.36.364 — Refactored. Previously used `transform: scale(1.2)` on
 * a 33px native radio to visually reach ~40px. Now the global
 * `input[type="radio"]` rule (updated in 0.36.364) renders a canonical
 * 40×40 radio with a 20px near-black dot via appearance:none + CSS,
 * so no scale hack is needed. `.scaleWrap` dropped; radio sits
 * directly as a flex child of the row.
 *
 * Two size variants per Henaket spec:
 *   size='standard' (default) → 40×40 radio, 16px gap, no row padding
 *   size='small'              → 24×24 radio, 8px gap, 8px vertical
 *                                row padding (row stays 40px total)
 *
 * Props:
 *   value         — currently-selected option value (string)
 *   onChange(v)   — called with new value when user clicks one
 *   options       — Array<{value, label}>
 *   name          — radio group name (links all radios)
 *   disabled      — disables the entire group
 *   ariaInvalid   — sets aria-invalid on each input
 *   size          — 'standard' | 'small' (default 'standard')
 *   orientation   — 0.36.481. 'vertical' (default, stacks rows in a
 *                   column) | 'horizontal' (rows in a row, wraps if
 *                   the group runs out of width). Horizontal is used
 *                   for short-label pickers like language codes where
 *                   all options fit inline.
 */
export default function FormRadioGroup({
  value,
  onChange,
  options = [],
  name,
  disabled = false,
  ariaInvalid = false,
  size = 'standard',
  orientation = 'vertical',
  /* 0.47.59 - No top space: for a group right under a configuration
     label, whose own small rows already carry 8px padding. */
  flushTop = false,
}) {
  const isSmall = size === 'small';
  const isHorizontal = orientation === 'horizontal';
  const groupClass = [styles.group, isHorizontal && styles.groupHorizontal, flushTop && styles.groupFlushTop]
    .filter(Boolean).join(' ');
  const rowClass = [styles.row, isSmall && styles.rowSmall]
    .filter(Boolean).join(' ');
  const inputClass = [styles.input, isSmall && styles.inputSmall]
    .filter(Boolean).join(' ');

  return (
    <div className={groupClass} role="radiogroup">
      {options.map((opt, idx) => {
        const id = `${name || 'radio'}-${idx}-${opt.value}`;
        const checked = opt.value === value;
        return (
          <label key={opt.value} className={rowClass} htmlFor={id}>
            <input
              id={id}
              className={inputClass}
              type="radio"
              name={name}
              value={opt.value}
              checked={checked}
              disabled={disabled}
              aria-invalid={ariaInvalid}
              onChange={() => onChange?.(opt.value)}
            />
            <span className={styles.label}>{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}
